/**
 * Client-seitiger In-Memory-Cache für Video-Modal-Profildaten.
 *
 * Zweck: Beim Hover über eine VideoCard wird das Profil bereits prefetcht.
 * Wenn der User dann klickt und das Modal öffnet, sind die Daten sofort da –
 * kein sichtbarer Lade-Spinner.
 *
 * TTL: 60 Sekunden – danach wird bei Bedarf erneut geladen.
 */

interface CacheEntry {
  data: unknown;
  fetchedAt: number; // Date.now()
  promise: Promise<unknown>;
}

const CACHE_TTL_MS = 60_000; // 60 s

const cache = new Map<string, CacheEntry>();

function cacheKey(profileId: string, locale: string) {
  return `${profileId}__${locale}`;
}

/**
 * Startet einen Prefetch-Request für das Profil (feuert-und-vergisst wenn bereits gecacht).
 * Wird im VideoCard beim Hover aufgerufen.
 */
export function prefetchVideoProfile(profileId: string, locale: string): void {
  const key = cacheKey(profileId, locale);
  const existing = cache.get(key);

  // Noch frisch → nicht erneut laden
  if (existing && Date.now() - existing.fetchedAt < CACHE_TTL_MS) return;

  const promise = fetch(`/api/videos/${profileId}?locale=${locale}`)
    .then((res) => {
      if (!res.ok) throw new Error('Not found');
      return res.json();
    })
    .catch(() => null);

  cache.set(key, { data: undefined, fetchedAt: Date.now(), promise });

  // Daten in Entry speichern sobald aufgelöst
  promise.then((data) => {
    const entry = cache.get(key);
    if (entry) entry.data = data;
  });
}

/**
 * Gibt gecachte Profildaten zurück falls vorhanden und noch frisch.
 * Gibt das Promise zurück wenn der Fetch noch läuft (optimistisch warten).
 * Gibt null zurück wenn kein Cache-Eintrag vorhanden.
 */
export function getCachedVideoProfile(
  profileId: string,
  locale: string,
): { data: unknown; ready: boolean } | { promise: Promise<unknown> } | null {
  const key = cacheKey(profileId, locale);
  const existing = cache.get(key);
  if (!existing) return null;

  // Abgelaufen
  if (Date.now() - existing.fetchedAt >= CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }

  // Bereits aufgelöst
  if (existing.data !== undefined) {
    return { data: existing.data, ready: true };
  }

  // Noch am Laden → Promise mitgeben (Modal kann darauf warten)
  return { promise: existing.promise };
}
